import {useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {GetRentalOrderApiResponse} from '@dms/api/rental';
import {GetRentalVehicleApiResponse, useGetRentalPriceGroupQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, useTenant, useVatRatesOptions} from '@dms/shared';

import {calculateCommonPrice} from '../../../utils/calculateCommonPrice';
import {calculateRentPrice} from '../../RentVehicleForm/utils/calculateRentPrice';

const skippedStates: GetRentalOrderApiResponse['state'][] = ['draft', 'canceled'];

export const useRentalOrderTotalPrice = (
  rentalOrder: GetRentalOrderApiResponse | undefined,
  rentalVehicle: GetRentalVehicleApiResponse | undefined
) => {
  const formatPrice = useFormatCurrency();
  const {tenantCurrency} = useTenant();
  const [vatRates] = useVatRatesOptions();

  const isReserved = rentalOrder?.state === 'reserved';

  const {data: defaultPriceGroup} = useGetRentalPriceGroupQuery(
    {rentalPriceGroupId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId ?? ''},
    {
      skip: isNil(rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId) || !isReserved,
    }
  );

  const activePriceGroupItem = defaultPriceGroup?.items.find(
    (item) =>
      item.rentalPriceGroupItemId === rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId
  );

  const {dailyRate, paidDays} = calculateRentPrice(activePriceGroupItem, rentalOrder);

  if (isNil(rentalOrder) || skippedStates.includes(rentalOrder.state)) {
    return {primaryParameter: undefined, secondaryParameter: undefined};
  }

  const reservedRentalOrderPrice = calculateCommonPrice(
    (dailyRate ?? 0) * (paidDays ?? 0),
    vatRates
  );

  const priceWithVat =
    rentalOrder.returnVehicle?.rentPrice?.summaryPrice?.withVat ??
    rentalOrder.rentVehicle?.rentPrice?.summaryPrice?.withVat ??
    reservedRentalOrderPrice?.withVat;
  const priceWithoutVat =
    rentalOrder.returnVehicle?.rentPrice?.summaryPrice?.withoutVat ??
    rentalOrder.rentVehicle?.rentPrice?.summaryPrice?.withoutVat ??
    reservedRentalOrderPrice?.withoutVat;

  const primaryParameter = isNotNil(priceWithVat)
    ? (formatPrice(Number(priceWithVat), tenantCurrency) ?? EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;

  const secondaryParameter = isNotNil(priceWithoutVat)
    ? `${formatPrice(Number(priceWithoutVat), tenantCurrency)} ${i18n.t('general.labels.withoutVat')}`
    : EMPTY_PLACEHOLDER;

  return {primaryParameter, secondaryParameter};
};
